import { useId } from 'react';

import { Typography, Grid, useTheme, useMediaQuery, Box } from '@mui/material';
import { format } from 'date-fns';

import { Maybe } from '@/types';
import { BlogDetailsPage, ImageAsset as TImageAsset } from '@/types/generated';
import TAGLink from '@/components/TAGLink';
import RichText, { RichTextContent } from '@/components/RichText';
import { BlogImage, IBlogDetailsPage } from '@/components/BlogDetailsPage';
import { useAppContext } from '@/context/AppContextProvider.ctx';

import TAGButton from '../TAGButton';

export type ICardDesignType = Maybe<'Standard' | 'Rounded Compact'>;

import {
	blogItemBoxStyles,
	blogItemButtonStyles,
	blogItemCategoryPillStyle,
	blogItemCompactImageStyles,
	blogItemContentStyles,
	blogItemGridLeftColStyles,
	blogItemGridRightColStyles,
	blogItemGridStyles,
	blogItemGridWideColStyles,
	blogItemRoundedCompactStyles,
	getBlogItemImageStyles,
	getBlogItemImageWrapperStyles,
} from './BlogList.styles';
import BlogImageCondition from './BlogImageCondition';

export interface IBlogListItemProps {
	blogDetails: BlogDetailsPage;
	buttonLabel: string;
	cardDesignType?: ICardDesignType;
}

interface ITileContentProps extends IBlogListItemProps {
	title: string;
	blogItemLink: string;
	blogImage: BlogImage;
	imageAsset: TImageAsset;
	isMobileScreen: boolean;
	path: string;
}

const DefaultTileContent = (props: Readonly<ITileContentProps>) => {
	const { title, blogItemLink, blogImage, imageAsset, isMobileScreen, path, buttonLabel, blogDetails } = props;
	const labelId = useId();
	return (
		<TAGLink
			href={blogItemLink}
			title={title}
			linkSx={blogItemBoxStyles}
			dataTestId={`blog_list_item_link_${path}`}
			ariaLabelledby={labelId}
		>
			<Box sx={getBlogItemImageWrapperStyles}>
				<BlogImageCondition
					imageAsset={imageAsset}
					blogImage={blogImage}
					imageAssetStyles={getBlogItemImageStyles}
					blogImageStyles={getBlogItemImageStyles}
					dataTestId={
						imageAsset
							? `blog_list_item_img_asset_focal_point_image_${path}`
							: `blog_list_item_focal_point_image_${path}`
					}
				/>
			</Box>
			<Grid item sx={blogItemContentStyles} data-test-id="blog_list_item_content_wrapper">
				<Typography variant={isMobileScreen ? 'bodySmallBook' : 'bodyMediumBook'} color={'text.primary'}>
					{blogDetails.category?.name} | {blogDetails.timeToRead} min read
				</Typography>
				<Typography variant={isMobileScreen ? 'header3' : 'header4'} id={labelId}>
					{title}
				</Typography>
				{!isMobileScreen && (
					<RichText
						content={blogDetails.summary?.json as RichTextContent}
						docProps={{ color: 'text.secondary', variant: 'bodyLargeBook', textAlign: 'left' }}
					/>
				)}
			</Grid>
			<TAGButton
				size={isMobileScreen ? 'M' : 'S'}
				variant="secondaryDefault"
				sx={blogItemButtonStyles}
				tabIndex={-1}
				aria-label={`Learn More about ${title}`}
				component="span"
			>
				{buttonLabel}
			</TAGButton>
		</TAGLink>
	);
};

const RoundedCompactTileContent = (props: Readonly<ITileContentProps>) => {
	const { title, blogItemLink, blogImage, imageAsset, path, buttonLabel, blogDetails } = props;
	const labelId = useId();

	const { category, subCategory, timeToRead, summary, date } = blogDetails as IBlogDetailsPage;
	const formattedDate = format(new Date(date), 'MMMM dd, yyyy');

	return (
		<TAGLink
			href={blogItemLink}
			title={title}
			linkSx={blogItemRoundedCompactStyles}
			dataTestId={`blog_list_item_link_roundedcompact_${path}`}
			ariaLabelledby={labelId}
		>
			<Box sx={getBlogItemImageWrapperStyles}>
				<BlogImageCondition
					imageAsset={imageAsset}
					blogImage={blogImage}
					imageAssetStyles={getBlogItemImageStyles}
					blogImageStyles={blogItemCompactImageStyles}
					dataTestId={
						imageAsset
							? `blog_list_item_img_asset_focal_point_image_${path}`
							: `blog_list_item_focal_point_image_${path}`
					}
				/>
			</Box>
			<Box sx={blogItemGridStyles} data-test-id="blog_list_item_content_wrapper">
				<Box sx={blogItemGridLeftColStyles}>
					{category?.name && (
						<Typography
							sx={blogItemCategoryPillStyle}
							variant={'bodySmallSemiBold'}
							color={'text.secondary'}
						>
							{category.name}
						</Typography>
					)}
					{subCategory?.name && (
						<Typography
							sx={blogItemCategoryPillStyle}
							variant={'bodySmallSemiBold'}
							color={'text.secondary'}
						>
							{subCategory.name}
						</Typography>
					)}
				</Box>
				{!!timeToRead && (
					<Typography sx={blogItemGridRightColStyles} variant={'bodySmallBook'} color={'text.secondary'}>
						{timeToRead} min read
					</Typography>
				)}
				<Typography sx={blogItemGridWideColStyles} variant={'header3'} id={labelId}>
					{title}
				</Typography>
				{summary && (
					<Box sx={blogItemGridWideColStyles}>
						<RichText
							content={summary?.json as RichTextContent}
							docProps={{ variant: 'bodyLargeBook', color: 'text.secondary', textAlign: 'left' }}
						/>
					</Box>
				)}
				{formattedDate && (
					<Typography
						sx={{ ...blogItemGridLeftColStyles, alignSelf: 'end' }}
						variant={'bodySmallBook'}
						color={'text.primary'}
					>
						{formattedDate}
					</Typography>
				)}
				{buttonLabel && (
					<Typography
						sx={{ ...blogItemGridRightColStyles, alignSelf: 'end' }}
						className={'item-link-learn-label'}
						variant={'bodySmallBook'}
						color={'text.primary'}
					>
						{buttonLabel}
					</Typography>
				)}
			</Box>
		</TAGLink>
	);
};

export default function BlogListItem(props: Readonly<IBlogListItemProps>) {
	const { blogDetails, cardDesignType = '' } = props;

	const theme = useTheme();
	const isMobileScreen = useMediaQuery(theme.breakpoints.down('md'));

	const { config } = useAppContext();

	const title = blogDetails.title ?? '';
	const path = blogDetails.path ?? '';
	const blogRootPath = config.features.blogs.root;

	const blogItemLink = `${blogRootPath}${path}/`;

	const blogImage = blogDetails.blogImage as BlogImage;
	const isRoundedCompact = cardDesignType === 'Rounded Compact';
	const imageAsset = blogDetails.imageAsset as TImageAsset;

	const defaultProps = {
		...props,
		title,
		blogItemLink,
		blogImage,
		imageAsset,
		isMobileScreen,
		path,
	};

	return (
		<Grid item width={'100%'} data-test-id="blog_list_item_container">
			{isRoundedCompact ? (
				<RoundedCompactTileContent {...defaultProps} />
			) : (
				<DefaultTileContent {...defaultProps} />
			)}
		</Grid>
	);
}
